// CRM 購物 tab — one customer's POS receipts, refunds and exchanges, next to
// their grooming records (one CRM for grooming + retail).
import { useEffect, useMemo, useState } from "react";
import { zhEn, dateLocale } from "./posTier.js";
import { C, Card, Pill, Sheet, Empty, money } from "./posUi.jsx";
import { posErrorText } from "./posMath.js";
import { methodLabel } from "./posReceipt.js";
import { SaleDetail } from "./History.jsx";
import { fetchSales } from "../../lib/api.js";

export default function CustomerPurchases({ tenant, customerId, customerName, sym = "$" }) {
  const [sales, setSales] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    let alive = true;
    setSales(null);
    fetchSales(tenant.id, { customerId, limit: 200 }).then(r => alive && setSales(r)).catch(e => { if (alive) { setErr(posErrorText(e)); setSales([]); } });
    return () => { alive = false; };
  }, [tenant.id, customerId]);
  const byId = useMemo(() => Object.fromEntries((sales || []).map(s => [s.id, s])), [sales]);
  const stats = useMemo(() => {
    const list = sales || [];
    const net = list.reduce((s, x) => s + Number(x.total), 0);
    const retail = list.reduce((s, x) => s + (x.sale_lines || []).filter(l => l.kind === "product" || l.kind === "custom").reduce((a, l) => a + Number(l.total), 0) - Number(x.order_discount || 0), 0);
    const top = {};
    for (const x of list) for (const l of x.sale_lines || []) if (l.kind === "product") top[l.name] = (top[l.name] || 0) + Number(l.qty);
    const fav = Object.entries(top).filter(([, q]) => q > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { net, retail, orders: list.filter(x => x.kind === "sale").length, fav };
  }, [sales]);
  const customerById = (id) => id === customerId ? { id, name: customerName } : null;

  if (sales === null) return <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 30 }}>{zhEn("載入中…", "Loading…")}</p>;
  const open = openId ? byId[openId] : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{err}</p>}
      {sales.length > 0 && (
        <Card style={{ padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, textAlign: "center" }}>
            {[[zhEn("收銀單數", "POS sales"), stats.orders], [zhEn("消費淨額", "Net spend"), money(stats.net, sym)], [zhEn("零售", "Retail"), money(stats.retail, sym)]].map(([k, v]) => (
              <div key={k}><p style={{ margin: 0, fontSize: 11.5, fontWeight: 900, color: C.sub }}>{k}</p><p style={{ margin: 0, fontSize: 18, fontWeight: 900, color: C.ink }}>{v}</p></div>
            ))}
          </div>
          {stats.fav.length > 0 && <p style={{ margin: "10px 0 0", fontSize: 12.5, fontWeight: 800, color: C.sub }}>{zhEn("🛍 常買：", "🛍 Often buys:")}{stats.fav.map(([n, q]) => n + " ×" + q).join("、")}</p>}
        </Card>
      )}
      {sales.length === 0 ? <Empty icon="🛍️" title={zhEn("未有收銀紀錄", "No POS history")} sub={zhEn("喺收銀揀呢位客戶結帳，就會喺度出現", "Check out with this customer at the till and it will appear here")} />
        : sales.map(s => (
          <Card key={s.id} onClick={() => setOpenId(s.id)} style={{ padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 900, color: C.ink }}>#{s.number}</span>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: C.sub }}>{new Date(s.created_at).toLocaleString(dateLocale(), { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              {s.kind === "refund" && <Pill tone="red">{zhEn("退款", "Refund")}</Pill>}
              {s.kind === "exchange" && <Pill tone="blue">{zhEn("換貨", "Exchange")}</Pill>}
              {s.status === "partial_refund" && <Pill tone="amber">{zhEn("部分退款", "Part refund")}</Pill>}
              {s.status === "refunded" && <Pill>{zhEn("已退款", "Refunded")}</Pill>}
              <span style={{ flex: 1 }} />
              <span style={{ fontWeight: 900, color: Number(s.total) < 0 ? C.red : C.ink }}>{money(s.total, sym)}</span>
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(s.sale_lines || []).map(l => (l.kind === "appointment" ? "✂️ " : "") + l.name).join("、")}</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 700, color: C.faint }}>{(s.sale_payments || []).map(p => methodLabel(p) + " " + money(p.amount, sym)).join(" · ")}{Number(s.points_earned) ? " · ⭐ " + s.points_earned : ""}</p>
          </Card>
        ))}
      <Sheet open={!!open} onClose={() => setOpenId(null)} title={open ? zhEn("單 #", "Sale #") + open.number : ""}>
        {open && <SaleDetail sale={open} sym={sym} tenant={tenant} customerById={customerById} byId={byId} canRefund={false} onRefund={() => {}} onOpen={setOpenId} />}
      </Sheet>
    </div>
  );
}
